import { useMemo, useState } from "react";
import { Box, Drawer, styled } from "@mui/material";
import { Search, Check, Plus, X } from "lucide-react";
import { EntityThumb } from "@components/EntityThumb/EntityThumb";
import { tokens } from "@styles/tokens";

export interface PickerOption {
  value: string;
  label: string;
  /** Secondary line, e.g. a SKU or a location path. */
  sublabel?: string;
  /** Optional thumbnail (products); falsy renders the neutral box icon. */
  thumbUrl?: string | null;
  /** Optional colored dot (statuses). */
  dotColor?: string;
}

interface PickerSheetProps {
  open: boolean;
  title: string;
  options: PickerOption[];
  selectedValue?: string;
  searchPlaceholder?: string;
  onSelect: (value: string) => void;
  onClose: () => void;
  /** Optional footer action, e.g. "New Product". */
  actionLabel?: string;
  onAction?: () => void;
}

/**
 * A searchable selection sheet shared by the redesigned forms' picker fields
 * (Product / Location / Status). Bottom-anchored for thumb reach; filters in
 * memory; shows an optional thumbnail or status dot, a check on the current
 * value, and an optional "create new" action at the foot.
 */
export const PickerSheet = ({
  open,
  title,
  options,
  selectedValue,
  searchPlaceholder = "Search…",
  onSelect,
  onClose,
  actionLabel,
  onAction,
}: PickerSheetProps) => {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const filtered = useMemo(
    () =>
      q
        ? options.filter(
            (o) =>
              o.label.toLowerCase().includes(q) ||
              o.sublabel?.toLowerCase().includes(q)
          )
        : options,
    [options, q]
  );

  const handleClose = () => {
    setQuery("");
    onClose();
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: tokens.radius.xl,
          borderTopRightRadius: tokens.radius.xl,
          maxHeight: "85vh",
        },
      }}
    >
      <Sheet>
        <Header>
          <Title>{title}</Title>
          <CloseBtn type="button" onClick={handleClose} aria-label="Close">
            <X size={20} />
          </CloseBtn>
        </Header>

        <SearchBar>
          <Search size={18} />
          <SearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
          />
        </SearchBar>

        <List>
          {filtered.length === 0 ? (
            <Empty>No matches</Empty>
          ) : (
            filtered.map((o) => {
              const selected = o.value === selectedValue;
              return (
                <Option
                  key={o.value}
                  type="button"
                  data-testid="picker-option"
                  onClick={() => {
                    onSelect(o.value);
                    handleClose();
                  }}
                  $selected={selected}
                >
                  {o.dotColor ? (
                    <Dot style={{ backgroundColor: o.dotColor }} />
                  ) : o.thumbUrl !== undefined ? (
                    <EntityThumb url={o.thumbUrl} alt={o.label} size={36} />
                  ) : null}
                  <OptionText>
                    <OptionLabel>{o.label}</OptionLabel>
                    {o.sublabel && <OptionSub>{o.sublabel}</OptionSub>}
                  </OptionText>
                  {selected && <Check size={18} />}
                </Option>
              );
            })
          )}
        </List>

        {actionLabel && onAction && (
          <Action
            type="button"
            onClick={() => {
              onAction();
              handleClose();
            }}
          >
            <Plus size={18} />
            {actionLabel}
          </Action>
        )}
      </Sheet>
    </Drawer>
  );
};

const Sheet = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  maxHeight: "85vh",
  padding: `${tokens.space(2)}px ${tokens.space(4)}px`,
  paddingBottom: `calc(env(safe-area-inset-bottom) + ${tokens.space(3)}px)`,
  backgroundColor: theme.skuey.surface,
}));

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  paddingTop: tokens.space(1),
  paddingBottom: tokens.space(2),
});

const Title = styled("h3")(({ theme }) => ({
  margin: 0,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  fontWeight: tokens.font.weight.bold,
  color: theme.skuey.ink,
}));

const CloseBtn = styled("button")(({ theme }) => ({
  display: "flex",
  border: "none",
  background: "none",
  padding: 0,
  cursor: "pointer",
  color: theme.skuey.muted,
  "&:hover": { color: theme.skuey.ink },
}));

const SearchBar = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(2),
  height: 44,
  marginBottom: tokens.space(2),
  padding: `0 ${tokens.space(3)}px`,
  borderRadius: tokens.radius.md,
  backgroundColor: theme.skuey.surfaceAlt,
  border: `1px solid ${theme.skuey.border}`,
  color: theme.skuey.subtle,
  flexShrink: 0,
}));

const SearchInput = styled("input")(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  border: "none",
  outline: "none",
  background: "transparent",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.ink,
  "&::placeholder": { color: theme.skuey.subtle, opacity: 1 },
}));

const List = styled(Box)({
  flex: 1,
  minHeight: 0,
  overflow: "auto",
});

const Empty = styled("p")(({ theme }) => ({
  margin: 0,
  padding: tokens.space(5),
  textAlign: "center",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  color: theme.skuey.muted,
}));

const Option = styled("button", {
  shouldForwardProp: (p) => p !== "$selected",
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: "flex",
  alignItems: "center",
  gap: tokens.space(2.5),
  width: "100%",
  padding: `${tokens.space(2.5)}px ${tokens.space(2)}px`,
  border: "none",
  background: "none",
  cursor: "pointer",
  textAlign: "left",
  borderRadius: tokens.radius.sm,
  color: $selected ? theme.skuey.brand[600] : theme.skuey.body,
  backgroundColor: $selected ? theme.skuey.brandSubtleBg : "transparent",
  "&:hover": {
    backgroundColor: $selected ? theme.skuey.brandSubtleBg : theme.skuey.surfaceAlt,
  },
}));

const Dot = styled(Box)({
  width: 10,
  height: 10,
  flexShrink: 0,
  borderRadius: tokens.radius.full,
});

const OptionText = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: 1,
  flex: 1,
  minWidth: 0,
});

const OptionLabel = styled("span")({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.medium,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const OptionSub = styled("span")(({ theme }) => ({
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.micro[0],
  color: theme.skuey.muted,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const Action = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space(2),
  width: "100%",
  height: 44,
  marginTop: tokens.space(2),
  flexShrink: 0,
  border: `1px dashed ${theme.skuey.borderStrong}`,
  borderRadius: tokens.radius.md,
  background: "none",
  cursor: "pointer",
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.small[0],
  fontWeight: tokens.font.weight.semibold,
  color: theme.skuey.brand[600],
  "&:hover": { backgroundColor: theme.skuey.brandSubtleBg },
}));
